import React from 'react';
import { Plus, Trash2, Clipboard, ArrowUpDown, RefreshCw, TableProperties } from 'lucide-react';
import { GanttTask } from '../types/gantt';
import { formatDateToIso, addDays } from '../utils/dateUtils';

interface TableEditorProps {
  tasks: GanttTask[];
  onTasksChange: (tasks: GanttTask[]) => void;
  onOpenPasteModal: () => void;
  onReloadExample: () => void;
  isExcel?: boolean;
  onImportFromExcel?: () => void;
}

export const TableEditor: React.FC<TableEditorProps> = ({
  tasks,
  onTasksChange,
  onOpenPasteModal,
  onReloadExample,
  isExcel = false,
  onImportFromExcel,
}) => {
  const handleTaskFieldChange = (
    index: number,
    field: keyof GanttTask,
    value: string | number
  ) => {
    const updated = [...tasks];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    onTasksChange(updated);
  };

  const handleAddTask = () => {
    const today = formatDateToIso(new Date());
    const newTask: GanttTask = {
      id: `task-${Date.now()}`,
      activity: `Activity ${tasks.length + 1}`,
      start: today,
      end: addDays(today, 7),
      progress: 100,
    };
    onTasksChange([...tasks, newTask]);
  };

  const handleDeleteTask = (index: number) => {
    const updated = tasks.filter((_, i) => i !== index);
    onTasksChange(updated);
  };

  const handleSortByDate = () => {
    const sorted = [...tasks].sort((a, b) => {
      const aTime = new Date(a.start).getTime() || 0;
      const bTime = new Date(b.start).getTime() || 0;
      return aTime - bTime;
    });
    onTasksChange(sorted);
  };

  return (
    <div className="table-editor-container">
      <div className="table-toolbar">
        <div className="table-toolbar-left">
          <span className="table-title">Activities ({tasks.length})</span>
        </div>
        <div className="table-toolbar-right">
          {isExcel && onImportFromExcel && (
            <button
              onClick={onImportFromExcel}
              className="btn btn-primary btn-sm"
              title="Read tasks directly from selected cells in active Excel sheet"
            >
              <TableProperties size={13} />
              <span>Read Selection</span>
            </button>
          )}
          <button
            onClick={onReloadExample}
            className="btn btn-secondary btn-sm"
            title="Reload example project with current dates"
          >
            <RefreshCw size={13} />
            <span>Reload Example</span>
          </button>
          <button
            onClick={onOpenPasteModal}
            className="btn btn-secondary btn-sm"
            title="Paste from Excel or Markdown"
          >
            <Clipboard size={13} />
            <span>Smart Paste</span>
          </button>
          <button
            onClick={handleSortByDate}
            className="btn btn-secondary btn-sm"
            title="Sort tasks chronologically"
          >
            <ArrowUpDown size={13} />
            <span>Sort</span>
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="task-grid">
          <thead>
            <tr>
              <th style={{ width: '38%' }}>Activity / Task</th>
              <th style={{ width: '22%' }}>Start Date</th>
              <th style={{ width: '22%' }}>End Date</th>
              <th style={{ width: '10%', textAlign: 'center' }}>Progress</th>
              <th style={{ width: '8%', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-state-cell">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '12px' }}>
                    <span>No activities found in table.</span>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                      {isExcel && onImportFromExcel && (
                        <button onClick={onImportFromExcel} className="btn btn-primary btn-sm">
                          <TableProperties size={13} />
                          <span>Import from Excel Selection</span>
                        </button>
                      )}
                      <button onClick={onReloadExample} className="btn btn-secondary btn-sm">
                        <RefreshCw size={13} />
                        <span>Load Example Data</span>
                      </button>
                      <button onClick={handleAddTask} className="btn btn-secondary btn-sm">
                        <Plus size={13} />
                        <span>Add Empty Row</span>
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              tasks.map((task, index) => (
                <tr key={task.id || index}>
                  <td>
                    <input
                      type="text"
                      className="input-cell"
                      value={task.activity}
                      placeholder="e.g. Planning, Testing..."
                      onChange={(e) =>
                        handleTaskFieldChange(index, 'activity', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      className="input-cell"
                      value={task.start}
                      onChange={(e) =>
                        handleTaskFieldChange(index, 'start', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      className="input-cell"
                      value={task.end}
                      onChange={(e) =>
                        handleTaskFieldChange(index, 'end', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        className="input-cell"
                        style={{ textAlign: 'right', padding: '5px 4px' }}
                        value={task.progress !== undefined ? task.progress : 100}
                        onChange={(e) => {
                          const val = e.target.value === '' ? 100 : parseInt(e.target.value, 10);
                          handleTaskFieldChange(
                            index,
                            'progress',
                            isNaN(val) ? 100 : Math.min(100, Math.max(0, val))
                          );
                        }}
                      />
                      <span style={{ fontSize: '11px', color: '#605E5C' }}>%</span>
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => handleDeleteTask(index)}
                      className="btn-icon btn-danger"
                      title="Delete activity"
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <button onClick={handleAddTask} className="btn btn-secondary btn-sm">
          <Plus size={15} />
          <span>Add Activity</span>
        </button>
      </div>
    </div>
  );
};
